import { Box, Grid, Typography } from '@mui/material';

import { useFormContext } from 'react-hook-form';

import { MemberPaymentMethod } from './components/MemberPaymentMethod.tsx/index.ts';
import { TakePaymentFormData, takePaymentFormDefault } from '../../../../helpers/TakePaymentSchema.ts';

import PaymentDescriptionField from './components/PaymentDescriptionField/PaymentDescriptionField.tsx';
import PaymentDateField from './components/PaymentDateField/PaymentDateField.tsx';
import PaymentTotalField from './components/PaymentTotalField/PaymentTotalField.tsx';
import PaymentAmountField from './components/PaymentAmountField/PaymentAmountField.tsx';
import PaymentTaxableField from './components/PaymentTaxableField/PaymentTaxableField.tsx';
import PaymentCategoryTypeField from './components/PaymentCategoryTypeField/PaymentCategoryTypeField.tsx';
import { useLocationContext } from '~/contexts/location.context.tsx';
import { useMemberPaymentMethods } from '~/modules/contact/hooks/useMemberPaymentMethods.ts';
import usePaymentMethodsStore from '~/modules/contact/components/ContactDetails/components/TabsContainer/components/MembershipTab/components/PaymentMethodsSection/components/PaymentMethods/hooks/usePaymentMethodsStore.ts';
import useTakePaymentStore from '../../../../hooks/useTakePaymentStore.ts';
import { ContactNameAutocomplete, ContactType } from '~/modules/@shared/components/index.ts';
import { useEffect, useMemo } from 'react';
import { useGetMembersByLocationQuery } from '~/services/member.ts';

export default function TakePaymentForm() {
  const { locationId, isDea } = useLocationContext();
  const {
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useFormContext<TakePaymentFormData>();

  const memberId = watch('member_id');

  const { paymentMethods } = useMemberPaymentMethods({ locationId, memberId, isDea });
  const openPaymentMethodsModal = usePaymentMethodsStore((state) => state.open);
  const setMemberId = usePaymentMethodsStore((state) => state.setMemberId);
  const isOpen = useTakePaymentStore((state) => state.isOpen);

  const { data: rawAllMembers } = useGetMembersByLocationQuery({ locationId, registered: 0 });

  const memberName = useMemo(() => {
    const selectedMember = (rawAllMembers || []).find((member) => member.member_id === memberId);

    if (selectedMember) {
      return `${selectedMember.first_name} ${selectedMember.last_name}`;
    }
    return '';
  }, [memberId, rawAllMembers]);

  const handleOpenPaymentMethodsModal = () => {
    if (!memberId) return;

    setMemberId(memberId);
    openPaymentMethodsModal();
  };

  const handleSelectMember = (option: ContactType) => {
    setValue('member_id', option.id);
  };

  useEffect(() => {
    if (!isOpen) {
      reset(takePaymentFormDefault);
    }
  }, [reset, isOpen]);

  return (
    <Box>
      <Box sx={{ my: 2, mx: 2 }}>
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          <>
            <Grid item xs={12} sm={7}>
              <Typography variant="h3">Contact name</Typography>
            </Grid>
            <Grid item xs={12} sm={5}>
              <ContactNameAutocomplete
                defaultOption={memberId ? { id: memberId, name: memberName } : null}
                shouldLoadAllMembers={true}
                onSelect={handleSelectMember}
                error={errors.member_id?.message || ''}
              />
            </Grid>
          </>

          <Grid item xs={12} sm={7}>
            <Typography variant="h3">Payment method</Typography>
          </Grid>
          <Grid item xs={12} sm={5}>
            <MemberPaymentMethod
              paymentMethods={memberId ? paymentMethods : []}
              onOpenPaymentMethodsModal={handleOpenPaymentMethodsModal}
            />
          </Grid>

          {isDea ? (
            <></>
          ) : (
            <>
              <Grid item xs={12} sm={7}>
                <Typography variant="h3">Category</Typography>
              </Grid>
              <Grid item xs={12} sm={5}>
                <PaymentCategoryTypeField />
              </Grid>
            </>
          )}

          {!isDea && (
            <Grid item xs={12} sm={12}>
              <PaymentTaxableField />
            </Grid>
          )}

          <Grid item xs={12} sm={8}>
            <Typography variant="h3">Payment Amount</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <PaymentAmountField />
          </Grid>

          {!isDea && (
            <>
              <Grid item xs={12} sm={8}>
                <Typography variant="h3">Total</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <PaymentTotalField />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={8}>
            <Typography variant="h3">Date</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <PaymentDateField />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="h3">
              Description
              <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                (optional)
              </Typography>
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', ml: 0.5, display: 'flex', justifyContent: 'right' }}>
              {watch('description')?.length} / 250
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <PaymentDescriptionField />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
